import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, Save, Eye, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { TermsManagement } from './TermsManagement';
import KnowledgeBaseManagement from './KnowledgeBaseManagement';
import AgreementsManagement from './AgreementsManagement';
import ConfirmModal from './ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';

interface AdminPanelProps {
  onClose: () => void;
}

interface Brand {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  contact_info: any;
  terms_text: string | null;
}

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  created_at: string;
  brands?: Brand[];
}

interface Discount {
  id: string;
  code: string;
  type: string;
  value: number | null;
  base_value: number | null;
  bonus_value: number | null;
  applicable_to: string;
  waive_setup: boolean;
  condition: string | null;
  active: boolean;
  applies_to_services: string[] | null;
  min_spend: number | null;
  max_discount: number | null;
}

interface InstallmentPlan {
  id: string;
  name: string;
  milestones: any[];
  applicable_products: string[];
  applicable_brands: string[];
  active: boolean;
}

interface ServicePricing {
  id: string;
  brand_id: string;
  category: string;
  client_type: string | null;
  tier_key: string;
  name: string;
  price: string;
  additional_price: string | null;
  description: string | null;
  features: any[];
  show_description: boolean;
}

interface AdditionalPricing {
  id: string;
  brand_id: string;
  item_key: string;
  name: string;
  price: string;
  description: string | null;
  features: any[];
}

interface SupportPlan {
  id: string;
  brand_id: string;
  tier_key: string;
  name: string;
  hours: number;
  price: string;
  description: string | null;
  features: any[];
}

export default function AdminPanel({ onClose }: AdminPanelProps) {
  const { profile } = useAuth();
  const toast = useToast();
  const { confirm, confirmState, closeConfirm } = useConfirm();
  const [activeTab, setActiveTab] = useState('brands');
  const [brands, setBrands] = useState<Brand[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [installments, setInstallments] = useState<InstallmentPlan[]>([]);
  const [estimates, setEstimates] = useState<any[]>([]);
  const [proposals, setProposals] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [editingInstallment, setEditingInstallment] = useState<InstallmentPlan | null>(null);
  const [editingEstimate, setEditingEstimate] = useState<any | null>(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', full_name: '', role: 'user' });
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [servicePricing, setServicePricing] = useState<ServicePricing[]>([]);
  const [additionalPricing, setAdditionalPricing] = useState<AdditionalPricing[]>([]);
  const [supportPlans, setSupportPlans] = useState<SupportPlan[]>([]);
  const [editingServicePricing, setEditingServicePricing] = useState<ServicePricing | null>(null);
  const [editingAdditionalPricing, setEditingAdditionalPricing] = useState<AdditionalPricing | null>(null);
  const [editingSupportPlan, setEditingSupportPlan] = useState<SupportPlan | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const isAdmin = profile?.role === 'admin';
  const isManager = profile?.role === 'manager';

  const tabs = [
    { id: 'brands', label: 'Brands', adminOnly: true },
    { id: 'users', label: 'Users', adminOnly: true },
    { id: 'pricing', label: 'Services', adminOnly: true },
    { id: 'knowledge', label: 'Knowledge Base', adminOnly: true },
    { id: 'agreements', label: 'Agreements', adminOnly: false },
    { id: 'products', label: 'Add-ons', adminOnly: true },
    { id: 'installments', label: 'Installments', adminOnly: true },
    { id: 'discounts', label: 'Discounts', adminOnly: false },
    { id: 'estimates', label: 'Estimates', adminOnly: false },
    { id: 'proposals', label: 'Proposals', adminOnly: false },
    { id: 'terms', label: 'Terms & Conditions', adminOnly: true },
  ];

  const visibleTabs = tabs.filter(tab => !tab.adminOnly || isAdmin);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  async function handleDeleteBrand(id: string) {
    const confirmed = await confirm({
      title: 'Delete Brand',
      message: 'Are you sure you want to delete this brand? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;
    const { error } = await supabase.from('brands').delete().eq('id', id);
    if (error) {
      toast.error(`Error deleting brand: ${error.message}`, 'Delete Error');
    } else {
      toast.success('Brand deleted successfully!', 'Brand Deleted');
      loadData();
    }
  }

  async function handleDeleteUser(id: string) {
    const confirmed = await confirm({
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;
    const { error } = await supabase.from('users').delete().eq('id', id);
    if (error) {
      toast.error(`Error deleting user: ${error.message}`, 'Delete Error');
    } else {
      toast.success('User deleted successfully!', 'User Deleted');
      loadData();
    }
  }

  async function handleDeleteDiscount(id: string) {
    const confirmed = await confirm({
      title: 'Delete Discount',
      message: 'Are you sure you want to delete this discount code? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;
    const { error } = await supabase.from('discount_codes').delete().eq('id', id);
    if (error) {
      toast.error(`Error deleting discount: ${error.message}`, 'Delete Error');
    } else {
      toast.success('Discount code deleted successfully!', 'Discount Deleted');
      loadData();
    }
  }

  async function handleDeleteInstallment(id: string) {
    const confirmed = await confirm({
      title: 'Delete Installment Plan',
      message: 'Are you sure you want to delete this installment plan? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;
    const { error } = await supabase.from('installment_plans').delete().eq('id', id);
    if (error) {
      toast.error(`Error deleting installment plan: ${error.message}`, 'Delete Error');
    } else {
      toast.success('Installment plan deleted successfully!', 'Plan Deleted');
      loadData();
    }
  }

  async function handleSaveBrand() {
    if (!editingBrand) return;
    const { error } = await supabase
      .from('brands')
      .update({
        name: editingBrand.name,
        primary_color: editingBrand.primary_color,
        secondary_color: editingBrand.secondary_color,
      })
      .eq('id', editingBrand.id);

    if (error) {
      alert(`Error updating brand: ${error.message}`);
    } else {
      setEditingBrand(null);
      loadData();
    }
  }

  async function handleSaveUser() {
    if (!editingUser) return;

    const { error: userError } = await supabase
      .from('users')
      .update({
        full_name: editingUser.full_name,
        role: editingUser.role,
      })
      .eq('id', editingUser.id);

    if (userError) {
      alert(`Error updating user: ${userError.message}`);
      return;
    }

    const { error: deleteError } = await supabase
      .from('user_brands')
      .delete()
      .eq('user_id', editingUser.id);

    if (deleteError) {
      alert(`Error updating brand assignments: ${deleteError.message}`);
      return;
    }

    if (selectedBrandIds.length > 0) {
      const brandAssignments = selectedBrandIds.map(brandId => ({
        user_id: editingUser.id,
        brand_id: brandId
      }));

      const { error: insertError } = await supabase
        .from('user_brands')
        .insert(brandAssignments);

      if (insertError) {
        alert(`Error saving brand assignments: ${insertError.message}`);
        return;
      }
    }

    setEditingUser(null);
    setSelectedBrandIds([]);
    loadData();
  }

  async function loadUserBrands(userId: string) {
    const { data, error } = await supabase
      .from('user_brands')
      .select('brand_id')
      .eq('user_id', userId);

    if (error) {
      console.error('Error loading user brands:', error);
      return [];
    }

    return data?.map(ub => ub.brand_id) || [];
  }

  async function handleSaveDiscount() {
    if (!editingDiscount) return;
    const { error } = await supabase
      .from('discount_codes')
      .update({
        code: editingDiscount.code,
        type: editingDiscount.type,
        value: editingDiscount.value,
        base_value: editingDiscount.base_value,
        bonus_value: editingDiscount.bonus_value,
        applicable_to: editingDiscount.applicable_to,
        waive_setup: editingDiscount.waive_setup,
        condition: editingDiscount.condition,
        active: editingDiscount.active,
        applies_to_services: editingDiscount.applies_to_services,
        min_spend: editingDiscount.min_spend,
        max_discount: editingDiscount.max_discount,
        applies_to_products: editingDiscount.applies_to_products,
        requires_products: editingDiscount.requires_products,
        discount_behavior: editingDiscount.discount_behavior,
      })
      .eq('id', editingDiscount.id);

    if (error) {
      alert(`Error updating discount: ${error.message}`);
    } else {
      setEditingDiscount(null);
      loadData();
    }
  }

  async function loadData() {
    setLoading(true);
    try {
      if (activeTab === 'brands') {
        const { data, error } = await supabase.from('brands').select('*').order('name');
        if (error) {
          console.error('Error loading brands:', error);
          alert(`Error loading brands: ${error.message}`);
        }
        setBrands(data || []);
      } else if (activeTab === 'users') {
        const { data: brandsData, error: brandsError } = await supabase
          .from('brands')
          .select('*')
          .order('name');

        if (brandsError) {
          console.error('Error loading brands:', brandsError);
        } else {
          setBrands(brandsData || []);
        }

        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('*')
          .order('email');

        if (usersError) {
          console.error('Error loading users:', usersError);
          alert(`Error loading users: ${usersError.message}`);
          setUsers([]);
        } else {
          const usersWithBrands = await Promise.all(
            (usersData || []).map(async (user) => {
              const { data: userBrandsData } = await supabase
                .from('user_brands')
                .select('brand_id, brands(*)')
                .eq('user_id', user.id);

              const brands = userBrandsData?.map(ub => ub.brands).filter(Boolean) || [];
              return { ...user, brands };
            })
          );
          setUsers(usersWithBrands);
        }
      } else if (activeTab === 'discounts') {
        const { data, error } = await supabase.from('discount_codes').select('*').order('code');
        if (error) {
          console.error('Error loading discounts:', error);
          alert(`Error loading discounts: ${error.message}`);
        }
        setDiscounts(data || []);

        const { data: pricingData, error: pricingError } = await supabase.from('service_pricing').select('*').order('category, name');
        if (pricingError) {
          console.error('Error loading pricing:', pricingError);
        }
        setServicePricing(pricingData || []);

        const { data: additionalData, error: additionalError } = await supabase.from('additional_pricing').select('*').order('name');
        if (additionalError) {
          console.error('Error loading additional pricing:', additionalError);
        }
        setAdditionalPricing(additionalData || []);

        const { data: supportData, error: supportError } = await supabase.from('support_plans').select('*').order('hours');
        if (supportError) {
          console.error('Error loading support plans:', supportError);
        }
        setSupportPlans(supportData || []);
      } else if (activeTab === 'installments') {
        const { data, error } = await supabase.from('installment_plans').select('*').order('name');
        if (error) {
          console.error('Error loading installments:', error);
          alert(`Error loading installments: ${error.message}`);
        }
        setInstallments(data || []);

        const { data: pricingData, error: pricingError } = await supabase.from('service_pricing').select('*').order('category, name');
        if (pricingError) {
          console.error('Error loading pricing:', pricingError);
        }
        setServicePricing(pricingData || []);
      } else if (activeTab === 'pricing') {
        const { data, error } = await supabase.from('service_pricing').select('*').order('category, name');
        if (error) {
          console.error('Error loading pricing:', error);
          alert(`Error loading pricing: ${error.message}`);
        }
        setServicePricing(data || []);

        const { data: supportData, error: supportError } = await supabase.from('support_plans').select('*').order('hours');
        if (supportError) {
          console.error('Error loading support plans:', supportError);
        }
        setSupportPlans(supportData || []);
      } else if (activeTab === 'products') {
        const { data, error } = await supabase.from('additional_pricing').select('*').order('name');
        if (error) {
          console.error('Error loading products:', error);
          alert(`Error loading products: ${error.message}`);
        }
        setAdditionalPricing(data || []);
      } else if (activeTab === 'estimates') {
        const { data, error } = await supabase
          .from('estimates')
          .select('*, users(full_name, email), brands(name)')
          .order('created_at', { ascending: false });
        if (error) {
          console.error('Error loading estimates:', error);
          alert(`Error loading estimates: ${error.message}`);
        }
        setEstimates(data || []);
      } else if (activeTab === 'proposals') {
        const { data, error } = await supabase
          .from('proposal_responses')
          .select('*, estimates(id, client_name, totals, shareable_id, brands(name))')
          .order('created_at', { ascending: false });
        if (error) {
          console.error('Error loading proposals:', error);
          alert(`Error loading proposals: ${error.message}`);
        }
        setProposals(data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert(`Unexpected error: ${error}`);
    } finally {
      setLoading(false);
    }
  }

  function renderBrands() {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Brands</h3>
          <button className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Brand
          </button>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : brands.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No brands found</p>
        ) : (
          <div className="space-y-2">
            {brands.map(brand => (
              <div key={brand.id} className="p-4 border rounded-lg flex justify-between items-center hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  {brand.logo_url && (
                    <img src={brand.logo_url} alt={brand.name} className="w-10 h-10 object-contain" />
                  )}
                  <div>
                    <div className="font-semibold">{brand.name}</div>
                    <div className="text-sm text-gray-500 flex gap-2">
                      <span style={{ color: brand.primary_color }}>●</span>
                      <span style={{ color: brand.secondary_color }}>●</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingBrand(brand)}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteBrand(brand.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderUsers() {
    if (isAddingUser) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => {
                setIsAddingUser(false);
                setNewUser({ email: '', password: '', full_name: '', role: 'user' });
                setSelectedBrandIds([]);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold">Add New User</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="user@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password *</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Min 6 characters"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <input
                type="text"
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="John Doe"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Role</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="sales_rep">Sales Rep</option>
                <option value="manager">Manager</option>
                <option value="subadmin">Sub Admin</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Brands</label>
              <div className="flex flex-wrap gap-2">
                {brands.map(brand => (
                  <button
                    key={brand.id}
                    onClick={() => {
                      setSelectedBrandIds(prev =>
                        prev.includes(brand.id)
                          ? prev.filter(id => id !== brand.id)
                          : [...prev, brand.id]
                      );
                    }}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      selectedBrandIds.includes(brand.id)
                        ? 'bg-gray-800 text-white'
                        : 'border hover:bg-gray-50'
                    }`}
                  >
                    {brand.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  if (!newUser.email || !newUser.password || !newUser.full_name) {
                    alert('Please fill in all required fields');
                    return;
                  }
                  if (newUser.password.length < 6) {
                    alert('Password must be at least 6 characters');
                    return;
                  }

                  const { data: authData, error: signUpError } = await supabase.auth.signUp({
                    email: newUser.email,
                    password: newUser.password,
                    options: {
                      data: {
                        full_name: newUser.full_name,
                        role: newUser.role,
                      },
                    },
                  });

                  if (signUpError) {
                    alert(`Error creating user: ${signUpError.message}`);
                    return;
                  }

                  if (authData.user && selectedBrandIds.length > 0) {
                    const userBrandInserts = selectedBrandIds.map(brandId => ({
                      user_id: authData.user.id,
                      brand_id: brandId,
                    }));

                    const { error: brandError } = await supabase
                      .from('user_brands')
                      .insert(userBrandInserts);

                    if (brandError) {
                      console.error('Error assigning brands:', brandError);
                    }
                  }

                  setIsAddingUser(false);
                  setNewUser({ email: '', password: '', full_name: '', role: 'user' });
                  setSelectedBrandIds([]);
                  await loadData();
                }}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
              >
                Create User
              </button>
              <button
                onClick={() => {
                  setIsAddingUser(false);
                  setNewUser({ email: '', password: '', full_name: '', role: 'user' });
                  setSelectedBrandIds([]);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Users</h3>
          <button
            onClick={() => setIsAddingUser(true)}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : users.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No users found</p>
        ) : (
          <div className="space-y-2">
            {users.map(user => (
              <div key={user.id} className="p-4 border rounded-lg flex justify-between items-center hover:bg-gray-50">
                <div className="flex-1">
                  <div className="font-semibold">{user.full_name}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      user.role === 'subadmin' ? 'bg-blue-100 text-blue-700' :
                      user.role === 'manager' ? 'bg-gray-200 text-gray-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {user.role}
                    </span>
                    {user.brands && user.brands.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {user.brands.map(brand => (
                          <span key={brand.id} className="px-2 py-1 rounded text-xs bg-green-100 text-green-700 flex items-center gap-1">
                            {brand.logo_url && (
                              <img src={brand.logo_url} alt="" className="w-3 h-3 object-contain" />
                            )}
                            {brand.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      setEditingUser(user);
                      const userBrands = await loadUserBrands(user.id);
                      setSelectedBrandIds(userBrands);
                    }}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderDiscounts() {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Discount Codes</h3>
          <button
            onClick={async () => {
              const timestamp = Date.now().toString().slice(-6);
              const newDiscount = {
                brand_id: brands[0]?.id || '00000000-0000-0000-0000-000000000001',
                code: `CODE${timestamp}`,
                type: 'percentage',
                value: 10,
                base_value: null,
                bonus_value: null,
                applicable_to: 'both',
                waive_setup: false,
                condition: null,
                active: true,
                applies_to_services: null,
                applies_to_products: null,
                requires_products: null,
                discount_behavior: 'standalone',
                min_spend: null,
                max_discount: null,
              };
              const { data, error } = await supabase
                .from('discount_codes')
                .insert([newDiscount])
                .select()
                .single();
              if (error) {
                alert(`Error creating discount: ${error.message}`);
              } else {
                await loadData();
                setEditingDiscount(data);
              }
            }}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Discount
          </button>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : discounts.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No discount codes found</p>
        ) : (
          <div className="space-y-2">
            {discounts.map(discount => (
              <div key={discount.id} className="p-4 border rounded-lg flex justify-between items-center hover:bg-gray-50">
                <div className="flex-1">
                  <div className="font-semibold">{discount.code}</div>
                  <div className="text-sm text-gray-600">
                    {discount.type === 'percentage' && `${discount.value}% off`}
                    {discount.type === 'fixed' && `$${discount.value} off`}
                    {discount.type === 'conditional' && `Base: ${discount.base_value}%, Bonus: ${discount.bonus_value}%`}
                  </div>
                  <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-1">
                    <span className={`px-2 py-1 rounded ${discount.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {discount.active ? 'Active' : 'Inactive'}
                    </span>
                    {discount.waive_setup && (
                      <span className="px-2 py-1 rounded bg-gray-200 text-gray-800">Waives Setup</span>
                    )}
                    {discount.applies_to_products && discount.applies_to_products.length > 0 && (
                      <span className="px-2 py-1 rounded bg-purple-100 text-purple-700">
                        Product-Specific ({discount.applies_to_products.length})
                      </span>
                    )}
                    {discount.requires_products && discount.requires_products.length > 0 && (
                      <span className="px-2 py-1 rounded bg-orange-100 text-orange-700">
                        Combo Discount
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingDiscount(discount)}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteDiscount(discount.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderInstallments() {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Installment Plans</h3>
          <button
            onClick={async () => {
              const newPlan = {
                brand_id: brands[0]?.id || '00000000-0000-0000-0000-000000000001',
                name: 'New Payment Plan',
                active: true,
                milestones: [
                  { order: 1, percentage: 50, description: 'Initial payment' },
                  { order: 2, percentage: 50, description: 'Final payment' }
                ]
              };
              const { data, error } = await supabase
                .from('installment_plans')
                .insert([newPlan])
                .select()
                .single();
              if (error) {
                alert(`Error creating plan: ${error.message}`);
              } else {
                await loadData();
                setEditingInstallment(data);
              }
            }}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Plan
          </button>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : installments.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No installment plans found</p>
        ) : (
          <div className="space-y-2">
            {installments.map(plan => (
              <div key={plan.id} className="p-4 border rounded-lg flex justify-between items-center hover:bg-gray-50">
                <div>
                  <div className="font-semibold">{plan.name}</div>
                  <div className="text-sm text-gray-600">
                    {plan.milestones?.length || 0} payment milestones
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {plan.applicable_products && plan.applicable_products.length > 0 ? (
                      <span>Applies to: {plan.applicable_products.join(', ')}</span>
                    ) : (
                      <span>Applies to: All products</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    <span className={`px-2 py-1 rounded ${plan.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {plan.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingInstallment(plan)}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteInstallment(plan.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderPricing() {
    const categories = [
      { value: 'all', label: 'All Services' },
      { value: 'website_packages', label: 'Website Packages' },
      { value: 'web_addons', label: 'Website Add-ons' },
      { value: 'seo_services', label: 'SEO Services' },
      { value: 'email_services', label: 'Email Services' },
      { value: 'ad_platforms', label: 'Ad Platforms' },
      { value: 'cro_services', label: 'CRO Services' },
      { value: 'support_plans', label: 'Website Care & Development Plans' },
    ];

    const filteredPricing = selectedCategory === 'all'
      ? servicePricing
      : selectedCategory === 'support_plans'
      ? []
      : servicePricing.filter(p => p.category === selectedCategory);

    const showSupportPlans = selectedCategory === 'all' || selectedCategory === 'support_plans';

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Service Pricing</h3>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <div className="space-y-2">
            {filteredPricing.map(pricing => (
              <div key={pricing.id} className="p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold">{pricing.name}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs mr-2">
                        {pricing.category.replace(/_/g, ' ')}
                      </span>
                      {pricing.client_type && (
                        <span className="px-2 py-1 bg-gray-200 text-gray-800 rounded text-xs mr-2">
                          {pricing.client_type}
                        </span>
                      )}
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                        ${parseFloat(pricing.price).toLocaleString()}
                      </span>
                      {pricing.additional_price && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs ml-2">
                          + ${parseFloat(pricing.additional_price).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {pricing.description && (
                      <div className="text-xs text-gray-600 mt-2 line-clamp-2">{pricing.description}</div>
                    )}
                  </div>
                  <button
                    onClick={() => setEditingServicePricing(pricing)}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded ml-2"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {showSupportPlans && supportPlans.map(plan => (
              <div key={plan.id} className="p-4 border rounded-lg hover:bg-gray-50 bg-purple-50 border-purple-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold">{plan.name}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs mr-2">
                        Support Plan
                      </span>
                      <span className="px-2 py-1 bg-gray-200 text-gray-800 rounded text-xs mr-2">
                        {plan.hours} hours/month
                      </span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                        ${parseFloat(plan.price).toLocaleString()}/mo
                      </span>
                    </div>
                    {plan.description && (
                      <div className="text-xs text-gray-600 mt-2 line-clamp-2">{plan.description}</div>
                    )}
                  </div>
                  <button
                    onClick={() => setEditingSupportPlan(plan)}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded ml-2"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {!loading && filteredPricing.length === 0 && (!showSupportPlans || supportPlans.length === 0) && (
              <p className="text-gray-400 text-center py-8">No services found</p>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderProducts() {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Additional Products</h3>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : additionalPricing.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No products found</p>
        ) : (
          <div className="space-y-2">
            {additionalPricing.map(product => (
              <div key={product.id} className="p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold">{product.name}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                        ${parseFloat(product.price).toLocaleString()}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs ml-2">
                        {product.item_key}
                      </span>
                    </div>
                    {product.description && (
                      <div className="text-xs text-gray-600 mt-2 line-clamp-2">{product.description}</div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const features = Array.isArray(product.features) ? product.features : [];
                      setEditingAdditionalPricing({ ...product, features });
                    }}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded ml-2"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderEstimates() {
    if (editingEstimate) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => setEditingEstimate(null)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold">Edit Estimate</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Client Name</label>
              <input
                type="text"
                value={editingEstimate.client_name}
                onChange={(e) => setEditingEstimate({ ...editingEstimate, client_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Client Type</label>
              <select
                value={editingEstimate.client_type}
                onChange={(e) => setEditingEstimate({ ...editingEstimate, client_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="local">Local</option>
                <option value="ecommerce">E-commerce</option>
                <option value="b2b">B2B</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={editingEstimate.status}
                onChange={(e) => setEditingEstimate({ ...editingEstimate, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  const { error } = await supabase
                    .from('estimates')
                    .update({
                      client_name: editingEstimate.client_name,
                      client_type: editingEstimate.client_type,
                      status: editingEstimate.status,
                    })
                    .eq('id', editingEstimate.id);
                  if (error) {
                    alert(`Error updating estimate: ${error.message}`);
                  } else {
                    setEditingEstimate(null);
                    await loadData();
                  }
                }}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
              >
                Save Changes
              </button>
              <button
                onClick={() => setEditingEstimate(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const confirmed = await confirm({
                    title: 'Delete Estimate',
                    message: 'Are you sure you want to delete this estimate? This action cannot be undone.',
                    confirmLabel: 'Delete',
                    variant: 'danger'
                  });
                  if (confirmed) {
                    const { error } = await supabase
                      .from('estimates')
                      .delete()
                      .eq('id', editingEstimate.id);
                    if (error) {
                      toast.error(`Error deleting estimate: ${error.message}`, 'Delete Error');
                    } else {
                      toast.success('Estimate deleted successfully!', 'Estimate Deleted');
                      setEditingEstimate(null);
                      await loadData();
                    }
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 ml-auto"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Estimates</h3>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : estimates.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No estimates found</p>
        ) : (
          <div className="space-y-2">
            {estimates.map(estimate => (
              <div key={estimate.id} className="p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold text-lg">{estimate.client_name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {estimate.users?.full_name} ({estimate.users?.email})
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 capitalize">
                        {estimate.client_type}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        estimate.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                        estimate.status === 'sent' ? 'bg-gray-200 text-gray-800' :
                        estimate.status === 'accepted' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {estimate.status}
                      </span>
                      <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-700">
                        {estimate.brands?.name || 'Unknown Brand'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-right">
                      <div className="font-bold text-lg text-gray-900">
                        ${estimate.totals?.grandTotal?.toLocaleString() || estimate.totals?.total_due?.toLocaleString() || '0'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(estimate.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}?estimate=${estimate.shareable_id}`;
                        window.open(url, '_blank');
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="View Full Estimate"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingEstimate(estimate)}
                      className="p-2 text-gray-700 hover:bg-gray-100 rounded"
                      title="Quick Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {estimate.config && (
                  <div className="mt-3 pt-3 border-t text-sm text-gray-600">
                    <div className="flex flex-wrap gap-3">
                      {estimate.config.includeWeb && (
                        <span>Web Package: {estimate.config.pkg}</span>
                      )}
                      {estimate.config.includeMarketing && (
                        <>
                          {estimate.config.selectedAdPlatforms?.length > 0 && (
                            <span>Ads: {estimate.config.selectedAdPlatforms.join(', ')}</span>
                          )}
                          {estimate.config.seoTier && (
                            <span>SEO: {estimate.config.seoTier}</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderProposals() {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Proposal Responses</h3>
          <div className="text-sm text-gray-600">
            Total: {proposals.length}
          </div>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : proposals.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No proposal responses yet</p>
        ) : (
          <div className="space-y-2">
            {proposals.map(proposal => (
              <div key={proposal.id} className="p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="font-semibold text-lg">
                        {proposal.estimates?.client_name || 'Unknown Client'}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        proposal.status === 'accepted'
                          ? 'bg-green-100 text-green-700'
                          : proposal.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {proposal.status === 'accepted' ? '✓ Accepted' : proposal.status === 'rejected' ? '✗ Rejected' : 'Pending'}
                      </span>
                    </div>

                    <div className="space-y-1 text-sm text-gray-600">
                      {proposal.estimates?.brands?.name && (
                        <div>
                          <span className="font-medium">Brand:</span> {proposal.estimates.brands.name}
                        </div>
                      )}
                      {proposal.estimates?.totals?.grandTotal && (
                        <div>
                          <span className="font-medium">Total Value:</span> ${proposal.estimates.totals.grandTotal.toLocaleString()}
                        </div>
                      )}
                      {proposal.client_name && (
                        <div>
                          <span className="font-medium">Client Name:</span> {proposal.client_name}
                        </div>
                      )}
                      {proposal.client_email && (
                        <div>
                          <span className="font-medium">Client Email:</span> {proposal.client_email}
                        </div>
                      )}
                      {proposal.status === 'accepted' && proposal.signed_at && (
                        <div>
                          <span className="font-medium">Signed:</span> {new Date(proposal.signed_at).toLocaleString()}
                        </div>
                      )}
                      {proposal.status === 'rejected' && proposal.rejection_reason && (
                        <div className="mt-2 p-2 bg-red-50 rounded">
                          <div className="font-medium text-red-900">Rejection Reason:</div>
                          <div className="text-red-700 mt-1">{proposal.rejection_reason}</div>
                          {proposal.rejection_details && (
                            <div className="text-red-600 text-xs mt-1 italic">
                              "{proposal.rejection_details}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {proposal.status === 'accepted' && proposal.signature_data && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="text-xs text-gray-500 mb-2">Client Signature:</div>
                        <img
                          src={proposal.signature_data}
                          alt="Client Signature"
                          className="border rounded p-2 bg-white max-w-xs h-20 object-contain"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="text-right text-xs text-gray-500">
                      <div>Responded:</div>
                      <div>{new Date(proposal.created_at).toLocaleDateString()}</div>
                      <div>{new Date(proposal.created_at).toLocaleTimeString()}</div>
                    </div>
                    {proposal.estimates?.shareable_id && (
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}?estimate=${proposal.estimates.shareable_id}`;
                          window.open(url, '_blank');
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors flex items-center gap-1 text-sm"
                        title="View Full Estimate"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View Details</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderPlaceholder(tabName: string) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">{tabName} management coming soon</p>
        <p className="text-gray-400 text-sm mt-2">This feature is currently under development</p>
      </div>
    );
  }

  function renderContent() {
    switch (activeTab) {
      case 'brands':
        return renderBrands();
      case 'users':
        return renderUsers();
      case 'discounts':
        return renderDiscounts();
      case 'installments':
        return renderInstallments();
      case 'pricing':
        return renderPricing();
      case 'products':
        return renderProducts();
      case 'estimates':
        return renderEstimates();
      case 'proposals':
        return renderProposals();
      case 'terms':
        return <TermsManagement brands={brands} />;
      case 'knowledge':
        return brands.length > 0 ? (
          <KnowledgeBaseManagement brandId={brands[0].id} />
        ) : (
          <div className="text-center py-12 text-gray-500">Please select a brand first</div>
        );
      case 'agreements':
        return brands.length > 0 ? (
          <AgreementsManagement brandId={brands[0].id} />
        ) : (
          <div className="text-center py-12 text-gray-500">Please select a brand first</div>
        );
      default:
        return renderPlaceholder(activeTab);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Admin Panel</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex border-b">
          {visibleTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-gray-700 border-b-2 border-gray-800'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto p-6 min-h-[400px]">
          {renderContent()}
        </div>
      </div>

      {editingBrand && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Brand</h3>
              <button onClick={() => setEditingBrand(null)} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingBrand.name}
                  onChange={(e) => setEditingBrand({ ...editingBrand, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                <input
                  type="color"
                  value={editingBrand.primary_color}
                  onChange={(e) => setEditingBrand({ ...editingBrand, primary_color: e.target.value })}
                  className="w-full h-10 px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                <input
                  type="color"
                  value={editingBrand.secondary_color}
                  onChange={(e) => setEditingBrand({ ...editingBrand, secondary_color: e.target.value })}
                  className="w-full h-10 px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex gap-2 justify-end mt-6">
                <button
                  onClick={() => setEditingBrand(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBrand}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit User</h3>
              <button onClick={() => setEditingUser(null)} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  disabled
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={editingUser.full_name}
                  onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="sales_rep">Sales Rep</option>
                  <option value="manager">Manager</option>
                  <option value="subadmin">Sub Admin</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Brands</label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {brands.length === 0 ? (
                    <p className="text-gray-400 text-sm">No brands available</p>
                  ) : (
                    brands.map(brand => (
                      <label key={brand.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={selectedBrandIds.includes(brand.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBrandIds([...selectedBrandIds, brand.id]);
                            } else {
                              setSelectedBrandIds(selectedBrandIds.filter(id => id !== brand.id));
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <div className="flex items-center gap-2 flex-1">
                          {brand.logo_url && (
                            <img src={brand.logo_url} alt={brand.name} className="w-6 h-6 object-contain" />
                          )}
                          <span className="text-sm">{brand.name}</span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="flex gap-2 justify-end mt-6">
                <button
                  onClick={() => {
                    setEditingUser(null);
                    setSelectedBrandIds([]);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingDiscount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Discount</h3>
              <button onClick={() => setEditingDiscount(null)} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    value={editingDiscount.code}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={editingDiscount.type}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                    <option value="conditional">Conditional</option>
                  </select>
                </div>
              </div>

              {editingDiscount.type === 'conditional' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Value ($)</label>
                    <input
                      type="number"
                      value={editingDiscount.base_value || ''}
                      onChange={(e) => setEditingDiscount({ ...editingDiscount, base_value: parseFloat(e.target.value) || null })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bonus Value ($)</label>
                    <input
                      type="number"
                      value={editingDiscount.bonus_value || ''}
                      onChange={(e) => setEditingDiscount({ ...editingDiscount, bonus_value: parseFloat(e.target.value) || null })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {editingDiscount.type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                    </label>
                    <input
                      type="number"
                      value={editingDiscount.value || ''}
                      onChange={(e) => setEditingDiscount({ ...editingDiscount, value: parseFloat(e.target.value) || null })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  {editingDiscount.type === 'percentage' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Discount ($)</label>
                      <input
                        type="number"
                        value={editingDiscount.max_discount || ''}
                        onChange={(e) => setEditingDiscount({ ...editingDiscount, max_discount: parseFloat(e.target.value) || null })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="Optional"
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Applies To</label>
                <select
                  value={editingDiscount.applicable_to}
                  onChange={(e) => setEditingDiscount({ ...editingDiscount, applicable_to: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="both">Website & Marketing</option>
                  <option value="website">Website Only</option>
                  <option value="marketing">Marketing Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specific Service Categories (Optional)</label>
                <div className="border rounded-lg p-3 space-y-1 max-h-64 overflow-y-auto">
                  {(() => {
                    const categories = [
                      { key: 'website_packages', label: 'Website Packages', count: servicePricing.filter(s => s.category === 'website_packages').length },
                      { key: 'web_addons', label: 'Website Add-ons', count: servicePricing.filter(s => s.category === 'web_addons').length },
                      { key: 'seo_services', label: 'SEO Services', count: servicePricing.filter(s => s.category === 'seo_services').length },
                      { key: 'email_services', label: 'Email & SMS Services', count: servicePricing.filter(s => s.category === 'email_services').length },
                      { key: 'ad_platforms', label: 'Ad Platforms (PPC)', count: servicePricing.filter(s => s.category === 'ad_platforms').length },
                      { key: 'cro_services', label: 'CRO Services', count: servicePricing.filter(s => s.category === 'cro_services').length },
                      { key: 'support_plans', label: 'Website Care & Development Plans', count: supportPlans.length },
                    ];

                    return categories.map(cat => (
                      <div key={cat.key} className="border-b last:border-b-0 pb-2 last:pb-0">
                        <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={editingDiscount.applies_to_services?.includes(cat.key) || false}
                            onChange={(e) => {
                              const current = editingDiscount.applies_to_services || [];
                              const updated = e.target.checked
                                ? [...current, cat.key]
                                : current.filter(s => s !== cat.key);
                              setEditingDiscount({ ...editingDiscount, applies_to_services: updated.length > 0 ? updated : null });
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-medium">{cat.label}</span>
                          <span className="text-xs text-gray-500">({cat.count} {cat.count === 1 ? 'service' : 'services'})</span>
                        </label>
                        {editingDiscount.applies_to_services?.includes(cat.key) && (
                          <div className="ml-8 mt-1 space-y-1 text-xs text-gray-600">
                            {cat.key === 'support_plans' ? (
                              supportPlans.map(plan => (
                                <div key={plan.tier_key} className="px-2 py-1 bg-gray-50 rounded">
                                  • {plan.name} (${parseFloat(plan.price).toLocaleString()}/mo)
                                </div>
                              ))
                            ) : (
                              servicePricing
                                .filter(s => s.category === cat.key)
                                .map(service => (
                                  <div key={service.tier_key} className="px-2 py-1 bg-gray-50 rounded">
                                    • {service.name} {service.client_type && `(${service.client_type})`} (${parseFloat(service.price).toLocaleString()})
                                  </div>
                                ))
                            )}
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select service categories to apply discount. Leave unchecked to apply to all services in "Applies To" category above.
                  <br />
                  <strong>Note:</strong> "Ad Platforms" applies to both platform management fees AND the 10% ad spend management fee.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Spend ($)</label>
                  <input
                    type="number"
                    value={editingDiscount.min_spend || ''}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, min_spend: parseFloat(e.target.value) || null })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                  <input
                    type="text"
                    value={editingDiscount.condition || ''}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, condition: e.target.value || null })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., adChannels"
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-gray-900 mb-3">Product-Specific Discounts (Optional)</h4>
                <p className="text-sm text-gray-600 mb-3">Apply discount to specific products instead of broad categories</p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount applies to these products:</label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto bg-gray-50">
                    {[...servicePricing, ...additionalPricing].length > 0 ? (
                      <>
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Services</div>
                        {servicePricing
                          .reduce((acc: any[], item) => {
                            if (!acc.find(i => i.tier_key === item.tier_key)) {
                              acc.push(item);
                            }
                            return acc;
                          }, [])
                          .map(service => (
                            <label key={service.tier_key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                              <input
                                type="checkbox"
                                checked={editingDiscount.applies_to_products?.includes(service.tier_key) || false}
                                onChange={(e) => {
                                  const current = editingDiscount.applies_to_products || [];
                                  const updated = e.target.checked
                                    ? [...current, service.tier_key]
                                    : current.filter(s => s !== service.tier_key);
                                  setEditingDiscount({ ...editingDiscount, applies_to_products: updated.length > 0 ? updated : null });
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">{service.name} ({service.tier_key})</span>
                            </label>
                          ))}
                        <div className="text-xs font-semibold text-gray-500 uppercase mt-3 mb-2">Add-ons</div>
                        {additionalPricing.map(addon => (
                          <label key={addon.item_key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={editingDiscount.applies_to_products?.includes(addon.item_key) || false}
                              onChange={(e) => {
                                const current = editingDiscount.applies_to_products || [];
                                const updated = e.target.checked
                                  ? [...current, addon.item_key]
                                  : current.filter(s => s !== addon.item_key);
                                setEditingDiscount({ ...editingDiscount, applies_to_products: updated.length > 0 ? updated : null });
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{addon.name} ({addon.item_key})</span>
                          </label>
                        ))}
                      </>
                    ) : (
                      <p className="text-sm text-gray-400">No products available</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave unchecked for category-wide discounts</p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Requires these products (for combination discounts):</label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto bg-gray-50">
                    {[...servicePricing, ...additionalPricing].length > 0 ? (
                      <>
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Services</div>
                        {servicePricing
                          .reduce((acc: any[], item) => {
                            if (!acc.find(i => i.tier_key === item.tier_key)) {
                              acc.push(item);
                            }
                            return acc;
                          }, [])
                          .map(service => (
                            <label key={service.tier_key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                              <input
                                type="checkbox"
                                checked={editingDiscount.requires_products?.includes(service.tier_key) || false}
                                onChange={(e) => {
                                  const current = editingDiscount.requires_products || [];
                                  const updated = e.target.checked
                                    ? [...current, service.tier_key]
                                    : current.filter(s => s !== service.tier_key);
                                  setEditingDiscount({
                                    ...editingDiscount,
                                    requires_products: updated.length > 0 ? updated : null,
                                    discount_behavior: updated.length > 0 ? 'conditional' : 'standalone'
                                  });
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">{service.name} ({service.tier_key})</span>
                            </label>
                          ))}
                        <div className="text-xs font-semibold text-gray-500 uppercase mt-3 mb-2">Add-ons</div>
                        {additionalPricing.map(addon => (
                          <label key={addon.item_key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={editingDiscount.requires_products?.includes(addon.item_key) || false}
                              onChange={(e) => {
                                const current = editingDiscount.requires_products || [];
                                const updated = e.target.checked
                                  ? [...current, addon.item_key]
                                  : current.filter(s => s !== addon.item_key);
                                setEditingDiscount({
                                  ...editingDiscount,
                                  requires_products: updated.length > 0 ? updated : null,
                                  discount_behavior: updated.length > 0 ? 'conditional' : 'standalone'
                                });
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{addon.name} ({addon.item_key})</span>
                          </label>
                        ))}
                      </>
                    ) : (
                      <p className="text-sm text-gray-400">No products available</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Check products that must be selected for this discount to apply</p>
                  {editingDiscount.requires_products && editingDiscount.requires_products.length > 0 && (
                    <div className="mt-2 p-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-800">
                      <strong>Combination Discount:</strong> This discount will only apply when all required products are selected.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingDiscount.waive_setup}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, waive_setup: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">Waive Setup Fee</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingDiscount.active}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex gap-2 justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setEditingDiscount(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDiscount}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingInstallment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Installment Plan</h3>
              <button onClick={() => setEditingInstallment(null)} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingInstallment.name}
                  onChange={(e) => setEditingInstallment({ ...editingInstallment, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="installment-active"
                  checked={editingInstallment.active}
                  onChange={(e) => setEditingInstallment({ ...editingInstallment, active: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="installment-active" className="text-sm font-medium text-gray-700">Active</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apply to Website Packages</label>
                <div className="border rounded-lg p-3 space-y-2 bg-gray-50">
                  {servicePricing && servicePricing.length > 0 ? (
                    servicePricing
                      .filter(s => s.category === 'website_packages')
                      .reduce((acc: any[], item) => {
                        if (!acc.find(i => i.tier_key === item.tier_key)) {
                          acc.push(item);
                        }
                        return acc;
                      }, [])
                      .map((product: any) => (
                        <label key={product.tier_key} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                          <input
                            type="checkbox"
                            checked={(editingInstallment.applicable_products || []).includes(product.tier_key)}
                            onChange={(e) => {
                              const currentProducts = editingInstallment.applicable_products || [];
                              const newProducts = e.target.checked
                                ? [...currentProducts, product.tier_key]
                                : currentProducts.filter((p: string) => p !== product.tier_key);
                              setEditingInstallment({ ...editingInstallment, applicable_products: newProducts });
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">{product.name}</span>
                        </label>
                      ))
                  ) : (
                    <p className="text-sm text-gray-500">Loading products...</p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Leave all unchecked to apply to all products
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Milestones</label>
                <div className="space-y-3">
                  {(() => {
                    const totalPercentage = (editingInstallment.milestones || []).reduce((sum: number, m: any) =>
                      sum + (parseFloat(m.percentage) || 0), 0
                    );
                    return (editingInstallment.milestones || []).map((milestone: any, index: number) => {
                    const isLastItem = index === (editingInstallment.milestones || []).length - 1;
                    const showWarning = isLastItem && totalPercentage !== 100;

                    return (
                      <div key={index} className="p-3 border rounded-lg space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Payment {milestone.order || index + 1}</span>
                          <button
                            onClick={() => {
                              const newMilestones = (editingInstallment.milestones || []).filter((_: any, i: number) => i !== index);
                              setEditingInstallment({ ...editingInstallment, milestones: newMilestones });
                            }}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Percentage (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={milestone.percentage || ''}
                            onChange={(e) => {
                              const newMilestones = [...(editingInstallment.milestones || [])];
                              newMilestones[index] = { ...milestone, percentage: parseFloat(e.target.value) || 0 };
                              setEditingInstallment({ ...editingInstallment, milestones: newMilestones });
                            }}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="e.g., 40"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                          <input
                            type="text"
                            value={milestone.description || ''}
                            onChange={(e) => {
                              const newMilestones = [...(editingInstallment.milestones || [])];
                              newMilestones[index] = { ...milestone, description: e.target.value };
                              setEditingInstallment({ ...editingInstallment, milestones: newMilestones });
                            }}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="e.g., To Start, Design approval..."
                          />
                        </div>
                        {showWarning && (
                          <div className="text-xs text-red-600 bg-red-50 p-2 rounded font-medium">
                            Total: {totalPercentage}% - Must equal 100% to save
                          </div>
                        )}
                      </div>
                    );
                  });
                  })()}

                  {(editingInstallment.milestones || []).length > 0 && (() => {
                    const totalPercentage = (editingInstallment.milestones || []).reduce((sum: number, m: any) =>
                      sum + (parseFloat(m.percentage) || 0), 0
                    );
                    return (
                      <div className={`p-3 rounded-lg font-medium ${totalPercentage === 100 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        <div className="flex justify-between items-center">
                          <span>Total Percentage:</span>
                          <span className="text-lg">{totalPercentage}%</span>
                        </div>
                        {totalPercentage !== 100 && (
                          <div className="text-xs mt-1 font-normal">
                            {totalPercentage < 100 ? `${100 - totalPercentage}% remaining` : `${totalPercentage - 100}% over limit`}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <button
                    onClick={() => {
                      const currentMilestones = editingInstallment.milestones || [];
                      const nextOrder = currentMilestones.length + 1;
                      const newMilestones = [...currentMilestones, { order: nextOrder, percentage: 0, description: '' }];
                      setEditingInstallment({ ...editingInstallment, milestones: newMilestones });
                    }}
                    className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center justify-center gap-1 border-2 border-dashed border-gray-300"
                  >
                    <Plus className="w-4 h-4" />
                    Add Payment Milestone
                  </button>
                </div>
              </div>

              <div className="flex gap-2 justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setEditingInstallment(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!editingInstallment) return;
                    const totalPercentage = (editingInstallment.milestones || []).reduce((sum: number, m: any) =>
                      sum + (parseFloat(m.percentage) || 0), 0
                    );
                    if (totalPercentage !== 100) {
                      alert(`Cannot save: Total percentage is ${totalPercentage}%. All payments must add up to exactly 100%.`);
                      return;
                    }
                    const { error } = await supabase
                      .from('installment_plans')
                      .update({
                        name: editingInstallment.name,
                        active: editingInstallment.active,
                        milestones: editingInstallment.milestones,
                        applicable_products: editingInstallment.applicable_products || []
                      })
                      .eq('id', editingInstallment.id);
                    if (error) {
                      alert(`Error updating installment plan: ${error.message}`);
                    } else {
                      setEditingInstallment(null);
                      loadData();
                    }
                  }}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingServicePricing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Service Pricing</h3>
              <button onClick={() => setEditingServicePricing(null)} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editingServicePricing.name}
                    onChange={(e) => setEditingServicePricing({ ...editingServicePricing, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                  <input
                    type="number"
                    value={editingServicePricing.price}
                    onChange={(e) => setEditingServicePricing({ ...editingServicePricing, price: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {(editingServicePricing.category === 'ad_platforms') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Price ($)</label>
                  <input
                    type="number"
                    value={editingServicePricing.additional_price || ''}
                    onChange={(e) => setEditingServicePricing({ ...editingServicePricing, additional_price: e.target.value || null })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="For additional platforms"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingServicePricing.description || ''}
                  onChange={(e) => setEditingServicePricing({ ...editingServicePricing, description: e.target.value || null })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={4}
                  placeholder="Service description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Features (View Details)</label>
                <div className="space-y-2">
                  {(() => {
                    const features = editingServicePricing.features;

                    if (Array.isArray(features) && features.every((f: any) => typeof f === 'string')) {
                      return (
                        <>
                          {features.map((feature: string, index: number) => (
                            <div key={index} className="flex gap-2">
                              <input
                                type="text"
                                value={feature}
                                onChange={(e) => {
                                  const newFeatures = [...features];
                                  newFeatures[index] = e.target.value;
                                  setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                                }}
                                className="flex-1 px-3 py-2 border rounded-lg text-sm"
                                placeholder="Feature description..."
                              />
                              <button
                                onClick={() => {
                                  const newFeatures = features.filter((_: string, i: number) => i !== index);
                                  setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                                }}
                                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newFeatures = [...(features || []), ''];
                              setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                            }}
                            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Add Feature
                          </button>
                        </>
                      );
                    }

                    if (features && typeof features === 'object' && features.sections && Array.isArray(features.sections)) {
                      return (
                        <div className="space-y-4">
                          {features.sections.map((section: any, sIndex: number) => (
                            <div key={sIndex} className="border rounded-lg p-4 bg-gray-50">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex-1 space-y-2">
                                  <input
                                    type="text"
                                    value={section.title || ''}
                                    onChange={(e) => {
                                      const newFeatures = { ...features };
                                      newFeatures.sections[sIndex].title = e.target.value;
                                      setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                                    }}
                                    className="w-full px-3 py-2 border rounded-lg font-semibold text-sm"
                                    placeholder="Section Title"
                                  />
                                  <textarea
                                    value={section.description || ''}
                                    onChange={(e) => {
                                      const newFeatures = { ...features };
                                      newFeatures.sections[sIndex].description = e.target.value;
                                      setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                                    }}
                                    className="w-full px-3 py-2 border rounded-lg text-sm"
                                    placeholder="Section Description"
                                    rows={2}
                                  />
                                </div>
                                <button
                                  onClick={() => {
                                    const newFeatures = { ...features };
                                    newFeatures.sections = newFeatures.sections.filter((_: any, i: number) => i !== sIndex);
                                    setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                                  }}
                                  className="ml-2 px-2 py-2 text-red-600 hover:bg-red-100 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="space-y-2 ml-4">
                                <label className="text-xs font-medium text-gray-600">Items:</label>
                                {section.items && Array.isArray(section.items) && section.items.map((item: string, iIndex: number) => (
                                  <div key={iIndex} className="flex gap-2">
                                    <input
                                      type="text"
                                      value={item}
                                      onChange={(e) => {
                                        const newFeatures = { ...features };
                                        newFeatures.sections[sIndex].items[iIndex] = e.target.value;
                                        setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                                      }}
                                      className="flex-1 px-3 py-1.5 border rounded text-xs"
                                      placeholder="Feature item..."
                                    />
                                    <button
                                      onClick={() => {
                                        const newFeatures = { ...features };
                                        newFeatures.sections[sIndex].items = newFeatures.sections[sIndex].items.filter((_: string, i: number) => i !== iIndex);
                                        setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                                      }}
                                      className="px-2 py-1.5 text-red-600 hover:bg-red-50 rounded"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => {
                                    const newFeatures = { ...features };
                                    if (!newFeatures.sections[sIndex].items) newFeatures.sections[sIndex].items = [];
                                    newFeatures.sections[sIndex].items.push('');
                                    setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                                  }}
                                  className="px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 rounded flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Item
                                </button>
                              </div>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newFeatures = { ...features };
                              if (!newFeatures.sections) newFeatures.sections = [];
                              newFeatures.sections.push({ title: '', description: '', items: [] });
                              setEditingServicePricing({ ...editingServicePricing, features: newFeatures });
                            }}
                            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Add Section
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                        <p className="mb-2">No features. Choose a format:</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingServicePricing({ ...editingServicePricing, features: [''] });
                            }}
                            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Simple List
                          </button>
                          <button
                            onClick={() => {
                              setEditingServicePricing({ ...editingServicePricing, features: { sections: [{ title: '', description: '', items: [] }] } });
                            }}
                            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Structured Sections
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="show-desc"
                  checked={editingServicePricing.show_description}
                  onChange={(e) => setEditingServicePricing({ ...editingServicePricing, show_description: e.target.checked })}
                  className="w-4 h-4 mr-2"
                />
                <label htmlFor="show-desc" className="text-sm font-medium text-gray-700">Show Description</label>
              </div>

              <div className="flex gap-2 justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setEditingServicePricing(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!editingServicePricing) return;
                    const features = Array.isArray(editingServicePricing.features) &&
                      editingServicePricing.features.every((f: any) => typeof f === 'string')
                      ? editingServicePricing.features.filter((f: string) => f.trim() !== '')
                      : editingServicePricing.features;
                    const { error } = await supabase
                      .from('service_pricing')
                      .update({
                        name: editingServicePricing.name,
                        price: editingServicePricing.price,
                        additional_price: editingServicePricing.additional_price,
                        description: editingServicePricing.description,
                        features: features,
                        show_description: editingServicePricing.show_description,
                      })
                      .eq('id', editingServicePricing.id);
                    if (error) {
                      alert(`Error updating pricing: ${error.message}`);
                    } else {
                      setEditingServicePricing(null);
                      loadData();
                    }
                  }}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingAdditionalPricing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Product</h3>
              <button onClick={() => setEditingAdditionalPricing(null)} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editingAdditionalPricing.name}
                    onChange={(e) => setEditingAdditionalPricing({ ...editingAdditionalPricing, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                  <input
                    type="number"
                    value={editingAdditionalPricing.price}
                    onChange={(e) => setEditingAdditionalPricing({ ...editingAdditionalPricing, price: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingAdditionalPricing.description || ''}
                  onChange={(e) => setEditingAdditionalPricing({ ...editingAdditionalPricing, description: e.target.value || null })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={4}
                  placeholder="Product description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Features (View Details)</label>
                <div className="space-y-2">
                  {(Array.isArray(editingAdditionalPricing.features) ? editingAdditionalPricing.features : []).map((feature: string, index: number) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => {
                          const currentFeatures = Array.isArray(editingAdditionalPricing.features) ? editingAdditionalPricing.features : [];
                          const newFeatures = [...currentFeatures];
                          newFeatures[index] = e.target.value;
                          setEditingAdditionalPricing({ ...editingAdditionalPricing, features: newFeatures });
                        }}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        placeholder="Feature description..."
                      />
                      <button
                        onClick={() => {
                          const currentFeatures = Array.isArray(editingAdditionalPricing.features) ? editingAdditionalPricing.features : [];
                          const newFeatures = currentFeatures.filter((_: string, i: number) => i !== index);
                          setEditingAdditionalPricing({ ...editingAdditionalPricing, features: newFeatures });
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const currentFeatures = Array.isArray(editingAdditionalPricing.features) ? editingAdditionalPricing.features : [];
                      const newFeatures = [...currentFeatures, ''];
                      setEditingAdditionalPricing({ ...editingAdditionalPricing, features: newFeatures });
                    }}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Feature
                  </button>
                </div>
              </div>

              <div className="flex gap-2 justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setEditingAdditionalPricing(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!editingAdditionalPricing) return;
                    const currentFeatures = Array.isArray(editingAdditionalPricing.features) ? editingAdditionalPricing.features : [];
                    const { error } = await supabase
                      .from('additional_pricing')
                      .update({
                        name: editingAdditionalPricing.name,
                        price: editingAdditionalPricing.price,
                        description: editingAdditionalPricing.description,
                        features: currentFeatures.filter((f: string) => f.trim() !== ''),
                      })
                      .eq('id', editingAdditionalPricing.id);
                    if (error) {
                      alert(`Error updating product: ${error.message}`);
                    } else {
                      setEditingAdditionalPricing(null);
                      loadData();
                    }
                  }}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingSupportPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Support Plan</h3>
              <button onClick={() => setEditingSupportPlan(null)} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editingSupportPlan.name}
                    onChange={(e) => setEditingSupportPlan({ ...editingSupportPlan, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                  <input
                    type="number"
                    value={editingSupportPlan.price}
                    onChange={(e) => setEditingSupportPlan({ ...editingSupportPlan, price: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours per Month</label>
                <input
                  type="number"
                  value={editingSupportPlan.hours}
                  onChange={(e) => setEditingSupportPlan({ ...editingSupportPlan, hours: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingSupportPlan.description || ''}
                  onChange={(e) => setEditingSupportPlan({ ...editingSupportPlan, description: e.target.value || null })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={4}
                  placeholder="Plan description..."
                />
              </div>

              <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                <p className="font-medium mb-1">Complex Features Structure</p>
                <p className="text-xs">Support plan features use a complex structure and cannot be edited here. Use database tools for advanced feature editing.</p>
              </div>

              <div className="flex gap-2 justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setEditingSupportPlan(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!editingSupportPlan) return;
                    const { error } = await supabase
                      .from('support_plans')
                      .update({
                        name: editingSupportPlan.name,
                        price: editingSupportPlan.price,
                        hours: editingSupportPlan.hours,
                        description: editingSupportPlan.description,
                      })
                      .eq('id', editingSupportPlan.id);
                    if (error) {
                      alert(`Error updating support plan: ${error.message}`);
                    } else {
                      setEditingSupportPlan(null);
                      loadData();
                    }
                  }}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        variant={confirmState.variant}
      />
    </div>
  );
}
